// BI reports are Dashboard state views, not browser routes. Keep evidence links
// on the real Dashboard route and use the existing sidebar for report access.
export const GUIDE_ROUTES = {
  dashboard: '/Dashboard',
  manual: {
    dashboard: '/manual/dashboard-overview',
    reports: '/manual/working-with-reports',
    sales: '/manual/sales-report',
    forecast: '/manual/forecast-and-targets',
    orders: '/manual/orders-report',
    products: '/manual/product-sales-report',
    categories: '/manual/category-report',
    clients: '/manual/understanding-clients-report',
    engagement: '/manual/engagement-report',
    clientCredit: '/manual/manage-client-credit',
    promotions: '/manual/promotion-report',
    ordersAndClients: '/manual/understanding-orders-clients',
    orderHub: '/manual/manage-orders-orderhub',
    feedback: '/manual/review-order-feedback',
    locations: '/manual/manage-locations',
    locationSchedules: '/manual/manage-location-schedules',
    locationPos: '/manual/manage-location-pos',
    inventoryCostSupply: '/manual/understanding-inventory-cost-supply',
    productCostRecipes: '/manual/manage-product-cost-recipes',
  },
};

export const MANAGEMENT_GUIDE_LANGUAGE_STORAGE_KEY = 'kalopsia.managementGuide.language';

const englishContent = {
  ui: {
    languageSelectorLabel: 'Management Guide language',
    startHere: 'Start here',
    foundation: 'Foundation',
    explore: 'Explore',
    comingLater: 'Coming later',
    futureDomains: 'Coming later',
    topicsComingLater: '{count} future domains',
    topicComingLater: '{count} future domain',
    chooseLesson: 'Choose a lesson',
    lessons: 'Lessons',
    close: 'Close',
    lessonProgress: 'Lesson {current} of {total}',
    lessonNumber: 'Lesson {number}',
    lessonRange: 'Lessons {first}–{last}',
    chooseLessonCurrent: 'Choose lesson. Current: {title}',
    reasoningMethod: 'Reasoning method',
    afterVerify: 'After Verify',
    beforeYouDecide: 'Before you decide',
    useKalopsia: 'Use Kalopsia',
    lessonContext: 'For “{title}”',
    recommendedStartingArea: 'Recommended starting area',
    continueInvestigating: 'Continue investigating',
    openDashboard: 'Open Kalopsia Dashboard',
    relatedManuals: 'Related User Manual articles',
    previousLesson: 'Previous lesson',
    nextLesson: 'Next lesson',
    firstLesson: 'This is the first lesson',
    moreGuidance: 'More guidance coming later',
    ask: 'Ask',
    stageQuestions: 'What each stage asks',
    beginWithQuestions: 'Begin with three questions',
    futureTopics: 'Future learning areas',
    simpleExample: 'Simple example',
    comparisonPrompt: 'Ask what it should be compared with',
    deeperPrompt: 'Look one level deeper',
    naturalNextStep: 'Natural next step',
    pathSeparatorAria: 'to',
    skipToLesson: 'Skip to current lesson',
    guideNavigation: 'Management Guide lessons',
    lessonSequence: 'Lesson sequence',
    firstLessonAria: 'Previous lesson: this is the first lesson',
    moreGuidanceAria: 'Next lesson: more guidance coming later',
    guideComplete: 'Current guide complete',
    returnToStart: 'Return to Start Here',
    returnToStartAria: 'Current guide complete: return to Start Here',
  },
  navigation: {
    start: {
      key: 'start',
      title: 'How to look at the business',
    },
  },
  hero: {
    eyebrow: 'A calmer way to understand the business',
    title: 'Management Guide',
    statement: 'Start with a question, not a report.',
    supporting: 'This guide helps you understand what to look at, how to interpret it, and what to investigate next. Kalopsia provides evidence; the User Manual explains how to use the tools.',
  },
  foundation: [
    {
      number: '01',
      title: 'How is the business performing?',
      summary: 'Start with the facts and understand the overall result.',
      lessonId: 'lesson-performance',
      lessonKey: 'performance',
      reasoningStage: 'Observe',
    },
    {
      number: '02',
      title: 'Is this result normal?',
      summary: 'Give the result context using a meaningful comparison.',
      lessonId: 'lesson-comparison',
      lessonKey: 'comparison',
      reasoningStage: 'Compare',
    },
    {
      number: '03',
      title: 'Where is the difference coming from?',
      summary: 'Break the result into smaller, understandable parts before looking for causes.',
      lessonId: 'lesson-decomposition',
      lessonKey: 'decomposition',
      reasoningStage: 'Decompose',
    },
  ],
  reasoning: {
    prompt: 'What are you trying to understand or decide?',
    stages: [
      { name: 'Observe', question: 'What happened?' },
      { name: 'Compare', question: 'Is it different or unusual?' },
      { name: 'Decompose', question: 'Where is the difference?' },
      { name: 'Explain', question: 'What could explain it?' },
      { name: 'Decide', question: 'Does anything require action?' },
      { name: 'Verify', question: 'Did the action work?' },
    ],
    principle: 'Finding where a difference exists does not prove why it happened.',
    outcome: 'What did we learn?',
  },
  lessonOne: {
    id: 'lesson-performance',
    label: 'Lesson 1',
    title: 'How is the business performing?',
    lead: 'Begin with observable facts. Describe the result before trying to explain it.',
    exampleTitle: 'Consider more than one perspective',
    points: [
      'Write down the exact period you are looking at.',
      'Define the business scope: locations, channels, products, or customer population.',
      'Treat sales and orders as important signals, not the whole health of the restaurant.',
      'Look from more than one business perspective before forming a conclusion.',
    ],
    perspectives: [
      { name: 'Demand', question: 'Are customers buying?' },
      { name: 'Guest experience', question: 'Are we delivering what we promised?' },
      { name: 'Operations', question: 'Can the restaurant execute reliably?' },
      { name: 'Resources', question: 'Do we have the stock and capacity required?' },
      { name: 'Economics', question: 'Does the activity make economic sense?' },
    ],
    caution: 'Kalopsia does not measure every perspective completely. Use approved operational, financial, or local evidence when the system does not contain the answer.',
    evidence: {
      startArea: 'Dashboard',
      secondaryAreas: ['BI Report → Sales'],
      instruction: 'Open the Dashboard for an overall signal. For a fuller sales view, choose BI Report → Sales from the existing sidebar if it is available to your role.',
      manuals: [
        { label: 'Learn how to use the Dashboard', route: GUIDE_ROUTES.manual.dashboard },
        { label: 'Learn how to use the Sales report', route: GUIDE_ROUTES.manual.sales },
      ],
    },
  },
  lessonTwo: {
    id: 'lesson-comparison',
    label: 'Lesson 2',
    title: 'Is this result normal?',
    lead: 'A number by itself has limited meaning. Give it context before judging it.',
    comparisons: [
      'a comparable previous period',
      'the same weekday where relevant',
      'last year where meaningful',
      'an approved target',
    ],
    principles: ['Compare like with like.', 'Normal is not necessarily acceptable.'],
    example: {
      observation: 'Sales are 8% below the comparison period.',
      prompt: 'Before explaining why, ask whether:',
      checks: [
        'the comparison period is genuinely comparable;',
        'the same operating scope is included;',
        'the period is equally complete;',
        'an unusual event affected either period.',
      ],
    },
    evidence: {
      startArea: 'BI Report → Sales',
      secondaryAreas: ['Forecast, when an expected result or approved target is relevant'],
      instruction: 'From the Dashboard, choose BI Report → Sales for the comparison. Use Forecast only when an expected result or approved target is relevant.',
      manuals: [
        { label: 'Learn how to compare reports', route: GUIDE_ROUTES.manual.reports },
        { label: 'Learn how to use the Sales report', route: GUIDE_ROUTES.manual.sales },
        { label: 'Learn about Forecast and Targets', route: GUIDE_ROUTES.manual.forecast },
      ],
    },
  },
  lessonThree: {
    id: 'lesson-decomposition',
    label: 'Lesson 3',
    title: 'Where is the difference coming from?',
    lead: 'Break the result into smaller parts before looking for causes.',
    questions: [
      'Did the number of orders change?',
      'Did average order value change?',
      'Did both change?',
    ],
    path: ['Business', 'Location', 'Channel', 'Product', 'Order'],
    equation: {
      salesValue: 'Sales value',
      orderCount: 'Number of orders',
      averageOrderValue: 'Average order value',
      ariaLabel: 'Sales value equals number of orders multiplied by average order value',
    },
    principle: 'Decomposition tells us WHERE a difference appears. It does not prove WHY it happened.',
    nextStep: 'Form more than one plausible explanation. Then look for evidence that supports or contradicts each one.',
    evidence: {
      startArea: 'BI Report → Sales',
      secondaryAreas: ['Orders', 'Products', 'Categories'],
      instruction: 'From the Dashboard, use only the evidence relevant to the question: BI Report → Sales first, then Orders, Products, or Categories when you need that extra detail.',
      manuals: [
        { label: 'Learn how to use the Sales report', route: GUIDE_ROUTES.manual.sales },
        { label: 'Learn how to use the Orders report', route: GUIDE_ROUTES.manual.orders },
        { label: 'Learn how to use the Product Sales report', route: GUIDE_ROUTES.manual.products },
        { label: 'Learn how to use the Category report', route: GUIDE_ROUTES.manual.categories },
      ],
    },
  },
  domainGroups: [
    {
      key: 'sales-demand',
      name: 'Sales & Demand',
      lessons: [
        {
          number: '04',
          lessonKey: 'sales-components',
          title: 'Are sales changing because of fewer orders or a different order value?',
          summary: 'Separate order volume from the value of each order.',
          label: 'Lesson 4',
          lead: 'The same sales result can come from very different customer behavior.',
          reasoningStages: ['Decompose'],
          questions: [
            'Are there fewer orders?',
            'Is average order value lower?',
            'Did both change?',
            'Did one improve while the other deteriorated?',
          ],
          model: {
            type: 'sequence',
            ariaLabel: 'Sales value equals number of orders multiplied by average order value',
            items: ['Sales value', 'Number of orders', 'Average order value'],
            separators: ['=', '×'],
          },
          example: {
            heading: 'Sales are approximately 8% lower.',
            facts: [
              'Order count is approximately 11% lower.',
              'Average order value is approximately 3% higher.',
            ],
            conclusionLabel: 'Interpretation at this stage',
            conclusion: 'The principal difference is currently in order volume, not lower spend per order.',
            qualification: 'This locates the component of change. It does not explain why order volume is lower.',
          },
          principle: 'A better average order value does not automatically mean the business is performing better if many orders have been lost.',
          nextStep: 'Check whether the lower order volume is spread across the business or concentrated by location, channel, or time.',
          evidence: {
            startArea: 'BI Report → Sales',
            secondaryAreas: ['Orders', 'Products', 'Categories', 'Engagement'],
            instruction: 'Open Dashboard, then choose BI Report → Sales. Compare sales value, number of orders, and average order value for the same period and scope. Use Orders or the other listed reports only to test the next hypothesis.',
            manuals: [
              { label: 'Sales Report', route: GUIDE_ROUTES.manual.sales },
              { label: 'Orders Report', route: GUIDE_ROUTES.manual.orders },
              { label: 'Engagement Report', route: GUIDE_ROUTES.manual.engagement },
            ],
          },
        },
        {
          number: '05',
          lessonKey: 'demand-location',
          title: 'Where is demand changing?',
          summary: 'Find the locations, channels, and times that contain the difference.',
          label: 'Lesson 5',
          lead: 'A company-wide result can hide very different local patterns.',
          reasoningStages: ['Decompose'],
          questions: [
            'Is the difference spread across most locations?',
            'Is it concentrated in only a few locations?',
            'Does it appear mainly in one sales or delivery channel?',
            'Does it appear at a particular time or day pattern?',
          ],
          model: {
            type: 'sequence',
            ariaLabel: 'Business to location to channel to time',
            items: ['Business', 'Location', 'Channel', 'Time'],
            separators: ['→', '→', '→'],
          },
          example: {
            heading: 'Network sales are lower.',
            facts: [
              'Most of the decline comes from three locations.',
              'At those locations, the main difference is in delivery orders while in-store orders remain broadly stable.',
            ],
            conclusionLabel: 'Conclusion at this stage',
            conclusion: 'The investigation has been narrowed to delivery activity in those locations.',
            qualification: 'This does not yet distinguish customer demand, availability, scheduling, provider failure, restaurant execution, or another factor.',
          },
          principle: 'First locate the change. Then investigate why it happened.',
          nextStep: 'Check what customers are choosing within the affected locations and channels, then test operational explanations only where the evidence points.',
          evidence: {
            startArea: 'BI Report → Sales',
            secondaryAreas: ['Orders', 'Locations', 'Locations → Schedule', 'Locations → POS', 'OrderHub'],
            instruction: 'Open Dashboard, then choose BI Report → Sales for location, delivery/channel, and time evidence. Use Orders next. Review Locations and its Schedule or POS tabs, or use Magic → OrderHub, only when a specific operational hypothesis requires it.',
            manuals: [
              { label: 'Sales Report', route: GUIDE_ROUTES.manual.sales },
              { label: 'Orders Report', route: GUIDE_ROUTES.manual.orders },
              { label: 'Manage Locations', route: GUIDE_ROUTES.manual.locations },
              { label: 'Manage Location Schedules', route: GUIDE_ROUTES.manual.locationSchedules },
              { label: 'Manage Location POS', route: GUIDE_ROUTES.manual.locationPos },
            ],
          },
        },
        {
          number: '06',
          lessonKey: 'customer-choices',
          title: 'What are customers choosing differently?',
          summary: 'Read value, quantity, and share together before judging product demand.',
          label: 'Lesson 6',
          lead: 'Sales performance depends not only on how many customers buy, but also on what they choose.',
          reasoningStages: ['Decompose', 'Explain'],
          questions: [
            'Did absolute sales value change?',
            'Did the quantity sold change?',
            'Did the product or category share change?',
            'Does the pattern remain when the same period and scope are used?',
          ],
          model: {
            type: 'sequence',
            ariaLabel: 'Value is not quantity, quantity is not share, and share is not profitability',
            items: ['Value', 'Quantity', 'Share', 'Profitability'],
            separators: ['≠', '≠', '≠'],
          },
          example: {
            heading: 'A category share increased from 20% to 24%.',
            facts: ['Before saying demand grew, check the category’s absolute sales value and quantity.'],
            conclusionLabel: 'Possible interpretation',
            conclusion: 'Its share may have increased because the rest of the portfolio declined.',
            qualification: 'A top seller is not automatically the most profitable product.',
          },
          principle: 'Always check the absolute number before interpreting a percentage or share.',
          nextStep: 'If the change is real, form separate hypotheses about promotion, price, availability, stock, or customer preference. Test them rather than assuming one.',
          evidence: {
            startArea: 'BI Report → Products',
            secondaryAreas: ['Categories', 'Sales'],
            instruction: 'Open Dashboard, then choose BI Report → Products. Compare value and quantity, then use Categories for mix/share context and Sales for the business total. Profitability requires separate economic evidence.',
            manuals: [
              { label: 'Product Sales Report', route: GUIDE_ROUTES.manual.products },
              { label: 'Category Report', route: GUIDE_ROUTES.manual.categories },
              { label: 'Sales Report', route: GUIDE_ROUTES.manual.sales },
            ],
          },
        },
      ],
    },
    {
      key: 'customers',
      name: 'Customers',
      lessons: [
        {
          number: '07',
          lessonKey: 'client-activation',
          title: 'Do new registrations become purchasing clients?',
          summary: 'Judge first-purchase activation only after a registration cohort has had time to buy.',
          label: 'Lesson 7',
          lead: 'A registered account becomes purchase-active when it makes a first qualifying purchase. Account validation is a separate measure.',
          reasoningStages: ['Compare', 'Decompose'],
          questions: [
            'What share of registrations made a first qualifying purchase within the same elapsed-day window?',
            'How many clients have completed that window, and how many are still pending?',
            'Does the pattern change by registration cohort or current client-profile county?',
            'Are second and third purchases changing separately from first purchases?',
          ],
          model: {
            type: 'sequence',
            ariaLabel: 'Registration to first qualifying purchase to repeat purchase',
            items: ['Registration', 'First purchase', 'Repeat purchase'],
            separators: ['→', '→'],
          },
          example: {
            heading: 'A recent registration cohort has a low 30-day activation rate.',
            facts: ['Many clients are still pending because 30 days have not elapsed since they registered.'],
            conclusionLabel: 'Management question',
            conclusion: 'How does the rate look among clients who have had the full 30 days?',
            qualification: 'Pending clients are not failed conversions. Compare equally mature cohorts before judging a campaign or process.',
          },
          principle: 'A first purchase, account validation, and a repeat purchase are different milestones.',
          nextStep: 'Compare mature cohorts at the same elapsed time, then investigate where first-purchase or repeat-purchase progression differs.',
          evidence: {
            startArea: 'BI Report → Clients → Activation',
            secondaryAreas: ['BI Report → Clients → Cohorts', 'BI Report → Clients → Overview'],
            instruction: 'Open Dashboard, then BI Report → Clients → Activation for first purchase, eligible and pending counts, and repeat purchases. Use Cohorts to compare registration weeks after the same number of days; the county filter describes the client’s current profile address.',
            manuals: [
              { label: 'Understanding the Clients Report', route: GUIDE_ROUTES.manual.clients },
              { label: 'Working with Reports', route: GUIDE_ROUTES.manual.reports },
            ],
          },
        },
        {
          number: '08',
          lessonKey: 'returning-customers',
          title: 'Are customers coming back?',
          summary: 'Distinguish a first purchase, a returning purchaser, and longer-term retention.',
          label: 'Lesson 8',
          lead: 'Registration is not the same as customer value.',
          reasoningStages: ['Compare', 'Explain'],
          questions: [
            'What share of active clients made a second or later lifetime qualifying purchase in the selected period?',
            'Are first, second, and third purchases progressing for equally mature registration cohorts?',
            'Have recent clients had enough time to make another purchase?',
            'What purchasing activity is outside the registered-client population?',
          ],
          model: {
            type: 'sequence',
            ariaLabel: 'New customer to active customer to returning customer',
            items: ['New customer', 'Active customer', 'Returning customer'],
            separators: ['→', '→'],
          },
          example: {
            heading: 'Registrations increased strongly.',
            facts: ['The share of active clients returning for a second or later purchase did not increase at the same rate.'],
            conclusionLabel: 'Management question',
            conclusion: 'Are new purchases developing into repeat relationships?',
            qualification: 'A returning-purchaser count for one period is not a long-term retention rate. First-time and returning counts may overlap when a client buys more than once in that period.',
          },
          principle: 'A second purchase is a useful milestone, but it does not by itself prove loyalty.',
          nextStep: 'Use aggregate customer patterns first. Inspect individual client records only for a defined, authorized, privacy-respecting purpose.',
          evidence: {
            startArea: 'BI Report → Clients → Activation',
            secondaryAreas: ['BI Report → Clients → Cohorts', 'BI Report → Clients → Overview', 'Engagement'],
            instruction: 'Open Dashboard, then BI Report → Clients. Use Overview for active and returning purchasers, Activation for second and third purchases, and Cohorts to compare groups at equal time since registration. Engagement shows sales activity, not a count of returning clients.',
            manuals: [
              { label: 'Understanding the Clients Report', route: GUIDE_ROUTES.manual.clients },
              { label: 'Engagement Report', route: GUIDE_ROUTES.manual.engagement },
              { label: 'Understanding Orders and Clients', route: GUIDE_ROUTES.manual.ordersAndClients },
            ],
          },
        },
        {
          number: '09',
          lessonKey: 'client-engagement',
          title: 'Who is behind the sales activity?',
          summary: 'Read registered and anonymous orders, sales, and average ticket without treating transactions as people.',
          label: 'Lesson 9',
          lead: 'The Engagement report compares represented sales activity from registered and anonymous purchasers.',
          reasoningStages: ['Compare', 'Decompose'],
          questions: [
            'Did registered and anonymous order counts change in the same way?',
            'Is a change in sales share coming from order share, average ticket, or both?',
            'Does the pattern hold for the same period and sales locations?',
            'What can this report not tell us about distinct customers or visits?',
          ],
          model: {
            type: 'definitions',
            items: [
              { term: 'Order share', description: 'The registered share of recorded orders.' },
              { term: 'Sales share', description: 'The registered share of recorded sales value.' },
              { term: 'Average ticket', description: 'Sales divided by recorded orders, for each represented group.' },
            ],
          },
          example: {
            heading: 'Registered sales share rises while registered order share is flat.',
            facts: ['Compare average ticket for registered and anonymous activity before calling this broader engagement.'],
            conclusionLabel: 'Possible interpretation',
            conclusion: 'The mix of order values may have changed without more registered orders.',
            qualification: 'Orders count transactions and sales record their value. Neither measures distinct clients, visits, retention, or whether a campaign caused the change.',
          },
          principle: 'A higher registered share of sales does not prove that more distinct clients are buying.',
          nextStep: 'Use Clients for distinct registered-client purchase milestones. Compare its current profile-county scope carefully with Engagement’s sales-location scope.',
          evidence: {
            startArea: 'BI Report → Engagement',
            secondaryAreas: ['BI Report → Sales', 'BI Report → Clients'],
            instruction: 'Open Dashboard, then BI Report → Engagement. Check the period and Sales location, then compare registered and anonymous orders, sales, shares, and average ticket. Use Clients for distinct registered-client activity and Sales for the broader total.',
            manuals: [
              { label: 'Engagement Report', route: GUIDE_ROUTES.manual.engagement },
              { label: 'Understanding the Clients Report', route: GUIDE_ROUTES.manual.clients },
            ],
          },
        },
        {
          number: '10',
          lessonKey: 'client-credit',
          title: 'What is client credit telling us?',
          summary: 'Separate credit movements and outstanding balances from customer value or profit.',
          label: 'Lesson 10',
          lead: 'Client credit is a separately tracked financial balance. Its movement can inform an investigation, but it cannot explain customer behavior alone.',
          reasoningStages: ['Observe', 'Explain'],
          questions: [
            'How much credit was rewarded, used, or changed through other movements in the selected period?',
            'What is the displayed balance for currently non-erased accounts at the period end, and is an earlier date only an estimate?',
            'Are skipped automatic rewards or unusual movements worth reconciling with orders?',
            'What can credit activity not establish about repeat buying, profit, or cash?',
          ],
          model: {
            type: 'definitions',
            items: [
              { term: 'Rewarded / Used', description: 'Movements during the selected period.' },
              { term: 'Net', description: 'The signed sum of movements, including other types.' },
              { term: 'Total credit', description: 'Balance at the selected period end for accounts that are currently non-erased; historical dates are reconstructed estimates.' },
            ],
          },
          example: {
            heading: 'Rewarded credit rises while use remains low.',
            facts: ['The outstanding balance grows, but this alone does not show why clients are not using it.'],
            conclusionLabel: 'Management question',
            conclusion: 'Are the credit rules, eligible orders, and recorded movements consistent with what we expected?',
            qualification: 'Credit awarded or held is not incremental sales, cash, profit, or proof of loyalty. Historical balance estimates depend on complete credit history.',
          },
          principle: 'Reconcile credit as a balance and movement history before drawing commercial conclusions.',
          warning: 'Use aggregate evidence first. Individual lookup and any manual adjustment require the authorized finance and privacy workflow.',
          nextStep: 'Compare aggregate credit activity with orders and registered-client purchasing. Escalate any balance/history discrepancy through the authorized process.',
          evidence: {
            startArea: 'Magic → ClientCredit',
            secondaryAreas: ['BI Report → Clients', 'BI Report → Orders'],
            instruction: 'Open Magic → ClientCredit for Rewarded, Used, Net, Total credit, daily evolution, and reward exceptions. Read Total credit as an end-date balance; past dates are estimates. The Top 5 balance view is always current, even when a day is selected for activity. Use the linked manual for authorized client-level work.',
            manuals: [
              { label: 'Manage Client Credit', route: GUIDE_ROUTES.manual.clientCredit },
              { label: 'Understanding the Clients Report', route: GUIDE_ROUTES.manual.clients },
            ],
          },
        },
      ],
    },
    {
      key: 'operations',
      name: 'Operations',
      lessons: [
        {
          number: '11',
          lessonKey: 'delivery-reliability',
          title: 'Can the restaurant deliver what customers expect?',
          summary: 'Look for operational and customer evidence that challenges the sales result.',
          label: 'Lesson 11',
          lead: 'Good demand is not enough if the restaurant cannot execute reliably.',
          reasoningStages: ['Explain'],
          questions: [
            'Are customers able to order what we promise?',
            'Are orders progressing normally?',
            'Are cancellations, reversals, or exceptions increasing?',
            'Is a location or ordering channel unavailable?',
            'Is customer feedback contradicting the commercial result?',
          ],
          example: {
            heading: 'Sales are close to target.',
            facts: [
              'Customer feedback deteriorated.',
              'One location shows unusually high order exceptions.',
            ],
            conclusionLabel: 'Conclusion',
            conclusion: 'Commercial performance alone is not enough to declare the operation healthy.',
            qualification: 'Use each operational signal to test a specific hypothesis; do not combine every exception into one diagnosis.',
          },
          principle: 'A good sales result can hide a deteriorating customer experience or operational problem.',
          nextStep: 'Narrow the issue by location, channel, order pattern, or feedback theme. Use logs only as specialist corroboration after the primary evidence is clear.',
          evidence: {
            startArea: 'BI Report → Orders',
            secondaryAreas: ['OrderHub', 'Dashboard', 'Feedback'],
            instruction: 'Open Dashboard, then choose BI Report → Orders for aggregate exceptions. Use Magic → OrderHub for a defined order investigation and Magic → Feedback for rating or comment patterns. Keep Logs → POS and Logs → Platform for authorized specialist corroboration.',
            manuals: [
              { label: 'Orders Report', route: GUIDE_ROUTES.manual.orders },
              { label: 'Manage Orders in OrderHub', route: GUIDE_ROUTES.manual.orderHub },
              { label: 'Review Order Feedback', route: GUIDE_ROUTES.manual.feedback },
              { label: 'Dashboard Overview', route: GUIDE_ROUTES.manual.dashboard },
            ],
          },
        },
      ],
    },
    {
      key: 'economics',
      name: 'Economics',
      lessons: [
        {
          number: '12',
          lessonKey: 'economic-result',
          title: 'More sales — but are we actually better off?',
          summary: 'Separate revenue, represented direct cost, contribution, profit, and cash.',
          label: 'Lesson 12',
          lead: 'Revenue is not profit.',
          reasoningStages: ['Compare', 'Explain'],
          questions: [
            'Did revenue change?',
            'Did represented direct product cost change faster or slower than revenue?',
            'Did product mix change the represented contribution?',
            'Did margin percentage and margin in lei move in different directions?',
            'Which products, ingredient quantities, or ingredient-price changes have the largest financial impact?',
            'Which important costs or cash movements are not present in this evidence?',
          ],
          model: {
            type: 'definitions',
            items: [
              { term: 'Sales / Revenue', description: 'What customers paid.' },
              { term: 'Direct product cost', description: 'The represented cost of the products sold.' },
              { term: 'Contribution / gross margin', description: 'What remains after represented direct product cost, before many other business costs.' },
              { term: 'Margin percentage', description: 'Gross margin divided by net sales. The location average is weighted by sales, so products with a larger sales share have more influence.' },
              { term: 'Net profit', description: 'Requires additional costs and accounting information.' },
              { term: 'Cash', description: 'Is not the same thing as profit.' },
            ],
          },
          example: {
            heading: 'Monthly net sales rise from RON 100,000 to RON 120,000.',
            facts: [
              'Product cost rises from RON 60,000 to RON 78,000.',
              'Margin falls from 40% to 35%, while its value rises from RON 40,000 to RON 42,000.',
            ],
            conclusionLabel: 'Conclusion',
            conclusion: 'A lower margin percentage can accompany a higher margin in lei. Read both measures, then separate realised selling price, volume/product mix, and unit cost before deciding what to change.',
            qualification: 'The complete financial answer may require ERP, accounting, payroll, banking, or other specialist evidence outside Kalopsia.',
          },
          principle: 'Revenue tells you how much you sold. It does not tell you how much you earned.',
          distinctions: [
            'Products above or below the current location-average margin answer a different question from products driving the change since last month. Prioritise sales-weighted financial impact over an extreme percentage on a small-selling product.',
            'Ingredient Cost Qty versus Rcp Qty uses the current recipe benchmark and includes allocated losses and gains. A difference is a check to investigate, not proof that a particular product caused waste. Compare ingredient valuation with the previous month separately.',
            'The combo, channel, and promotion views describe the same sales from different perspectives. Keep their figures separate from the underlying-product totals.',
            'An unavailable figure is unknown; it must not be interpreted as zero. Check missing days, product costs, and comparison coverage before drawing a full-month conclusion.',
          ],
          nextStep: 'Start with the largest positive and negative drivers. Check the supporting price, volume/mix, cost, and ingredient evidence; choose two or three practical checks and verify them next month. Gross product margin excludes costs such as rent, salaries, and platform commissions, so it cannot establish net profit or cash.',
          evidence: {
            startArea: 'BI Report → Commercial → Margin',
            secondaryAreas: ['BI Report → Sales', 'BI Report → ProdCost → Product Margin, when authorized and the cost evidence is suitable'],
            instruction: 'For users with margin-report access, open Dashboard → BI Report → Commercial → Margin. Select one location, a full month with a registered month close, and the report language. The previous calendar month is compared when supported. Choose Generate PDF, then open saved results for the product comparison and separate ingredient, combo, channel, and promotion views. Download the English or Romanian PDF from saved history within 24 hours. Use Sales, Product Margin, and the cost/recipe views to check the relevant figures; review coverage when values or comparisons are unavailable.',
            manuals: [
              { label: 'Sales Report', route: GUIDE_ROUTES.manual.sales },
              { label: 'Understanding Inventory, Cost, and Supply', route: GUIDE_ROUTES.manual.inventoryCostSupply },
              { label: 'Manage Product Cost and Recipes', route: GUIDE_ROUTES.manual.productCostRecipes },
            ],
          },
        },
      ],
    },
    {
      key: 'commercial',
      name: 'Commercial',
      lessons: [
        {
          number: '13',
          lessonKey: 'commercial-objective',
          orderedQuestions: true,
          title: 'Did our commercial action achieve what we intended?',
          summary: 'Define the intended result before judging a promotion, price change, launch, or other commercial action.',
          label: 'Lesson 13',
          lead: 'A promotion, price change, product launch, availability decision, channel initiative, or customer campaign can only be judged against its intended result.',
          reasoningStages: ['Explain', 'Decide', 'Verify'],
          questions: [
            'What result did we expect: more orders or visits, new or reactivated customers, product trial, a mix change, stronger weak-period demand, or another defined goal?',
            'Which customers, products, locations, or channels were affected?',
            'What changed after the action?',
            'Compared with what?',
            'What else could explain the change?',
            'What was the economic or customer cost?',
            'Did anything else deteriorate?',
            'Should we continue, modify, stop, or investigate further?',
          ],
          example: {
            heading: 'A promotion shows 1,000 used claims and RON 80,000 of associated order value.',
            facts: [
              'Usage is not the same as success: a used promotion does not automatically mean it improved the business.',
              'This shows that customers used the promotion.',
              'It also shows that those orders were associated with the promotion.',
            ],
            limitLabel: 'This does not prove that:',
            limits: [
              'the orders would not have happened without the promotion;',
              'customer frequency increased;',
              'the promotion generated incremental sales;',
              'another product was not cannibalized;',
              'the discount was economically justified.',
            ],
            conclusionLabel: 'Return to the objective',
            conclusion: 'If the objective was product trial, ask whether customers who tried the product bought it again later. If the objective was stronger weak-period demand, compare that period with an appropriate baseline.',
            qualification: 'Kalopsia can show activity and association, but the current evidence does not provide a complete causal answer.',
          },
          principle: 'Define success before measuring it.',
          warning: 'Association does not prove causation.',
          nextStep: 'Choose evidence that matches the original objective, consider other explanations and costs, then decide whether to continue, modify, stop, or investigate further. Define how that decision will be verified.',
          evidence: {
            startArea: 'BI Report → Promotions',
            secondaryAreas: ['BI Report → Commercial', 'BI Report → Commercial → Margin', 'Sales', 'Orders', 'Clients', 'Engagement', 'Products', 'Categories'],
            instruction: 'Open Dashboard, then choose BI Report → Promotions for promotion use, associated order value, discounts, and open claims. Use Commercial → Products for a selected product, combo, or promotion over complete weeks. Use Commercial → Margin for a full closed month at one location: the Promotions view pairs recorded discounts and sales on qualifying orders with margin percentage, margin in lei, and previous-month changes. Each qualifying order is counted once, and discounts are already included in net sales. Check coverage and compare with the other reports according to the objective. These observations do not establish cause, incremental sales, or net profit.',
            manuals: [
              { label: 'Promotion Report', route: GUIDE_ROUTES.manual.promotions },
              { label: 'Dashboard Overview', route: GUIDE_ROUTES.manual.dashboard },
              { label: 'Sales Report', route: GUIDE_ROUTES.manual.sales },
              { label: 'Orders Report', route: GUIDE_ROUTES.manual.orders },
              { label: 'Understanding the Clients Report', route: GUIDE_ROUTES.manual.clients },
              { label: 'Engagement Report', route: GUIDE_ROUTES.manual.engagement },
            ],
          },
        },
      ],
    },
    {
      key: 'intelligence',
      name: 'Intelligence',
      lessons: [
        {
          number: '14',
          lessonKey: 'missing-signals',
          orderedQuestions: true,
          title: 'What might I be missing?',
          summary: 'Use unusual signals to direct attention, then return to normal management reasoning.',
          label: 'Lesson 14',
          lead: 'Managers naturally investigate what they already know to look for. Intelligence may identify another signal that deserves attention, but it does not replace understanding or judgment.',
          reasoningStages: ['Observe', 'Compare'],
          questions: [
            'Is the evidence reliable and sufficiently complete?',
            'Is the difference material enough to deserve attention?',
            'Where does it appear?',
            'What could explain it?',
            'What important information may be missing?',
            'Does it require action?',
            'How will we verify the decision?',
          ],
          model: {
            type: 'sequence',
            ariaLabel: 'Something unusual is detected, then check reliability, materiality, location, explanations, missing context, action, and verification',
            items: ['Unusual signal', 'Reliable evidence?', 'Material difference?', 'Where?', 'Possible explanations?', 'Missing context?', 'Action?', 'Verification?'],
            separators: ['→', '→', '→', '→', '→', '→', '→'],
          },
          example: {
            heading: 'Kalopsia identifies sales at one location as unusually low compared with the expected pattern.',
            facts: [
              'Do not conclude that demand decreased.',
              'Open the sales explanation to separate recorded transactions, average ticket, and available item-level evidence. Then check channels, availability, operating context, incomplete data, and local events.',
            ],
            conclusionLabel: 'What the signal tells us',
            conclusion: 'Attention may be useful at this location and time.',
            qualification: 'The insight is a starting signal. It does not explain the cause or replace investigation.',
          },
          distinctions: [
            'Unusual does not automatically mean bad.',
            'An anomaly does not explain its cause.',
            'An insight does not automatically justify an action.',
            'A transaction warning episode and a person’s review status answer different questions.',
          ],
          principle: 'Intelligence directs attention. Management still requires understanding.',
          warning: 'Observed sales drivers and an evidence-quality score are not a root cause or a probability. Ask what important information may exist outside the system, such as operating changes or local events.',
          nextStep: 'Return to the complete loop: Observe → Compare → Decompose → Explain → Decide → Verify. Test the signal with reliable evidence, consider missing context, take only a proportionate action, and define how you will verify it.',
          evidence: {
            startArea: 'Dashboard → intel → Intelligence inbox',
            secondaryAreas: ['Dashboard → trends', 'BI Report → Sales', 'BI Report → Orders', 'Locations'],
            instruction: 'Open Dashboard → intel for daily summaries, above- or below-expected sales signals, and Transactions warnings. Sales signals offer an observed sales explanation; transaction warnings can open Trends. Review status records human triage, while an ongoing or ended episode describes the recorded trend. Investigate with reports and local evidence before acting.',
            manuals: [
              { label: 'Dashboard Overview', route: GUIDE_ROUTES.manual.dashboard },
              { label: 'Sales Report', route: GUIDE_ROUTES.manual.sales },
              { label: 'Orders Report', route: GUIDE_ROUTES.manual.orders },
              { label: 'Product Sales Report', route: GUIDE_ROUTES.manual.products },
              { label: 'Working with Reports', route: GUIDE_ROUTES.manual.reports },
            ],
          },
        },
        {
          number: '15',
          lessonKey: 'sales-trends',
          title: 'Is a decline persistent or local?',
          summary: 'Use comparable completed weeks to see whether fewer transactions persist at a location.',
          label: 'Lesson 15',
          lead: 'A flat sales total can hide fewer transactions when average ticket is higher. A weekly trend helps locate this pattern.',
          reasoningStages: ['Compare', 'Decompose'],
          questions: [
            'Across the six completed weeks, how did sales, recorded transactions, and average ticket change?',
            'Were the same weekdays last year comparable, and how many weeks were included?',
            'Is the pattern specific to one location or also visible among comparable network peers?',
            'Is a warning criterion met, is an episode recorded, and what has a person reviewed?',
          ],
          model: {
            type: 'sequence',
            ariaLabel: 'Observed weekly change to coverage to episode history to investigation',
            items: ['Weekly change', 'Coverage', 'Episode history', 'Investigation'],
            separators: ['→', '→', '→'],
          },
          example: {
            heading: 'Sales are nearly flat, but transactions have fallen for several comparable weeks.',
            facts: ['Average ticket is higher, which can offset fewer recorded transactions in the sales total.'],
            conclusionLabel: 'What the trend tells us',
            conclusion: 'The location needs a closer look at transaction volume and the weeks where the change occurred.',
            qualification: 'Recorded transactions are not unique customers or footfall. The trend does not identify a cause, and a warning criterion is not the same as an open issue.',
          },
          distinctions: [
            'Excluded weeks are not zero activity.',
            'Fewer than four comparable weeks are insufficient for a new warning.',
            'An episode’s ongoing or ended state is separate from human review status.',
          ],
          principle: 'Check comparable-week coverage and the underlying transactions before interpreting a trend.',
          nextStep: 'Open the location’s Weeks and History evidence, then test plausible operational and demand explanations with Sales, Orders, and local context.',
          evidence: {
            startArea: 'Dashboard → trends → Sales trends',
            secondaryAreas: ['Dashboard → intel', 'BI Report → Sales', 'BI Report → Orders'],
            instruction: 'Open Dashboard → trends. Select a completed Sunday and location; compare six completed weeks with matching weekdays last year. Read Sales, Transactions, Avg ticket, and Coverage. When available, Vs network compares transaction change with at least five permitted same-company peers. Open Weeks and History for detail. Viewing Trends does not create an insight or change its review status.',
            manuals: [
              { label: 'Dashboard Overview', route: GUIDE_ROUTES.manual.dashboard },
              { label: 'Sales Report', route: GUIDE_ROUTES.manual.sales },
              { label: 'Orders Report', route: GUIDE_ROUTES.manual.orders },
            ],
          },
        },
      ],
    },
  ],
  futureDomains: [
    { name: 'Stock & Supply', description: 'Understand whether demand can be fulfilled and stock evidence is under control.' },
  ],
  reminder: [
    { name: 'Period', question: 'Am I looking at the correct time?' },
    { name: 'Scope', question: 'Am I looking at the correct locations or population?' },
    { name: 'Evidence', question: 'Is the information complete enough?' },
    { name: 'Context', question: 'Is there something important Kalopsia does not know?' },
  ],
};

const romanianContent = {
  ui: {
    languageSelectorLabel: 'Limba Ghidului de management',
    startHere: 'Începe aici',
    foundation: 'Fundamente',
    explore: 'Explorează',
    comingLater: 'În curând',
    futureDomains: 'În curând',
    topicsComingLater: '{count} domenii viitoare',
    topicComingLater: '{count} domeniu viitor',
    chooseLesson: 'Alege o lecție',
    lessons: 'Lecții',
    close: 'Închide',
    lessonProgress: 'Lecția {current} din {total}',
    lessonNumber: 'Lecția {number}',
    lessonRange: 'Lecțiile {first}–{last}',
    chooseLessonCurrent: 'Alege lecția. Acum: {title}',
    reasoningMethod: 'Mod de gândire',
    afterVerify: 'După verificare',
    beforeYouDecide: 'Înainte să decizi',
    useKalopsia: 'Folosește Kalopsia',
    lessonContext: 'Pentru „{title}”',
    recommendedStartingArea: 'De unde să începi',
    continueInvestigating: 'Unde continui investigația',
    openDashboard: 'Deschide Dashboard în Kalopsia',
    relatedManuals: 'Articole relevante din User Manual',
    previousLesson: 'Lecția anterioară',
    nextLesson: 'Lecția următoare',
    firstLesson: 'Aceasta este prima lecție',
    moreGuidance: 'Mai multe lecții vor fi adăugate',
    ask: 'Întreabă-te',
    stageQuestions: 'Ce întrebare pune fiecare etapă',
    beginWithQuestions: 'Începe cu trei întrebări',
    futureTopics: 'Domenii de învățare viitoare',
    simpleExample: 'Exemplu simplu',
    comparisonPrompt: 'Întreabă cu ce ar trebui comparat',
    deeperPrompt: 'Mergi cu un nivel mai jos',
    naturalNextStep: 'Următoarea întrebare firească',
    pathSeparatorAria: 'spre',
    skipToLesson: 'Sari la lecția curentă',
    guideNavigation: 'Lecțiile Ghidului de management',
    lessonSequence: 'Ordinea lecțiilor',
    firstLessonAria: 'Lecția anterioară: aceasta este prima lecție',
    moreGuidanceAria: 'Lecția următoare: mai multe lecții vor fi adăugate',
    guideComplete: 'Ai parcurs ghidul disponibil',
    returnToStart: 'Revino la Începe aici',
    returnToStartAria: 'Ai parcurs ghidul disponibil: revino la Începe aici',
  },
  navigation: {
    start: {
      key: 'start',
      title: 'Cum să privești afacerea',
    },
  },
  hero: {
    eyebrow: 'Un mod mai calm de a înțelege afacerea',
    title: 'Ghid de management',
    statement: 'Începe cu o întrebare, nu cu un raport.',
    supporting: 'Acest ghid te ajută să înțelegi la ce să te uiți, cum să interpretezi ceea ce vezi și ce să investighezi mai departe. Kalopsia oferă dovezi; User Manual explică modul de folosire a instrumentelor.',
  },
  foundation: [
    {
      number: '01',
      title: 'Cum merge afacerea?',
      summary: 'Începe cu faptele și înțelege rezultatul general.',
      lessonId: 'lesson-performance',
      lessonKey: 'performance',
      reasoningStage: 'Observă',
    },
    {
      number: '02',
      title: 'Este acest rezultat normal?',
      summary: 'Pune rezultatul în context printr-o comparație relevantă.',
      lessonId: 'lesson-comparison',
      lessonKey: 'comparison',
      reasoningStage: 'Compară',
    },
    {
      number: '03',
      title: 'De unde vine diferența?',
      summary: 'Împarte rezultatul în părți mai mici și ușor de înțeles înainte să cauți cauze.',
      lessonId: 'lesson-decomposition',
      lessonKey: 'decomposition',
      reasoningStage: 'Descompune',
    },
  ],
  reasoning: {
    prompt: 'Ce încerci să înțelegi sau să decizi?',
    stages: [
      { name: 'Observă', question: 'Ce s-a întâmplat?' },
      { name: 'Compară', question: 'Este diferit sau neobișnuit?' },
      { name: 'Descompune', question: 'Unde apare diferența?' },
      { name: 'Explică', question: 'Ce ar putea explica rezultatul?' },
      { name: 'Decide', question: 'Este nevoie de o acțiune?' },
      { name: 'Verifică', question: 'A funcționat acțiunea?' },
    ],
    principle: 'Faptul că ai găsit unde apare diferența nu înseamnă că ai aflat și de ce apare.',
    outcome: 'Ce am învățat?',
  },
  lessonOne: {
    id: 'lesson-performance',
    label: 'Lecția 1',
    title: 'Cum merge afacerea?',
    lead: 'Începe cu fapte observabile. Descrie rezultatul înainte să încerci să îl explici.',
    exampleTitle: 'Privește rezultatul din mai multe perspective',
    points: [
      'Notează perioada exactă pe care o analizezi.',
      'Definește ce parte a afacerii analizezi: locații, canale, produse sau grupuri de clienți.',
      'Privește vânzările și comenzile ca semnale importante, nu ca imaginea completă a sănătății restaurantului.',
      'Privește rezultatul din mai multe perspective înainte să tragi o concluzie.',
    ],
    perspectives: [
      { name: 'Cerere', question: 'Cumpără clienții?' },
      { name: 'Experiența oaspeților', question: 'Oferim experiența pe care am promis-o?' },
      { name: 'Operațiuni', question: 'Poate restaurantul să funcționeze constant și fără probleme?' },
      { name: 'Resurse', question: 'Avem stocul și capacitatea necesare?' },
      { name: 'Economie', question: 'Are sens economic această activitate?' },
    ],
    caution: 'Kalopsia nu măsoară complet fiecare perspectivă. Folosește dovezi operaționale, financiare sau locale aprobate atunci când răspunsul nu există în sistem.',
    evidence: {
      startArea: 'Dashboard',
      secondaryAreas: ['BI Report → Sales'],
      instruction: 'Deschide Dashboard pentru un semnal general. Pentru o vedere mai completă asupra vânzărilor, alege BI Report → Sales din meniul existent, dacă rolul tău permite accesul.',
      manuals: [
        { label: 'Learn how to use the Dashboard', route: GUIDE_ROUTES.manual.dashboard },
        { label: 'Learn how to use the Sales report', route: GUIDE_ROUTES.manual.sales },
      ],
    },
  },
  lessonTwo: {
    id: 'lesson-comparison',
    label: 'Lecția 2',
    title: 'Este acest rezultat normal?',
    lead: 'Un număr izolat spune puțin. Pune-l în context înainte să îl judeci.',
    comparisons: [
      'o perioadă anterioară comparabilă',
      'aceeași zi a săptămânii, când este relevant',
      'anul trecut, când comparația are sens',
      'o țintă aprobată',
    ],
    principles: ['Compară lucruri comparabile.', 'Normal nu înseamnă neapărat acceptabil.'],
    example: {
      observation: 'Sales este cu 8% sub perioada de comparație.',
      prompt: 'Înainte să explici de ce, întreabă dacă:',
      checks: [
        'perioada de comparație este cu adevărat comparabilă;',
        'este inclusă aceeași arie operațională;',
        'ambele perioade sunt la fel de complete;',
        'un eveniment neobișnuit a afectat una dintre perioade.',
      ],
    },
    evidence: {
      startArea: 'BI Report → Sales',
      secondaryAreas: ['Forecast, când este relevant un rezultat așteptat sau o valoare de referință aprobată în Targets'],
      instruction: 'Din Dashboard, alege BI Report → Sales pentru comparație. Folosește Forecast numai când este relevant un rezultat așteptat sau o valoare de referință aprobată în Targets.',
      manuals: [
        { label: 'Learn how to compare reports', route: GUIDE_ROUTES.manual.reports },
        { label: 'Learn how to use the Sales report', route: GUIDE_ROUTES.manual.sales },
        { label: 'Learn about Forecast and Targets', route: GUIDE_ROUTES.manual.forecast },
      ],
    },
  },
  lessonThree: {
    id: 'lesson-decomposition',
    label: 'Lecția 3',
    title: 'De unde vine diferența?',
    lead: 'Împarte rezultatul în părți mai mici înainte să cauți cauze.',
    questions: [
      'S-a schimbat numărul de comenzi?',
      'S-a schimbat valoarea medie a unei comenzi?',
      'S-au schimbat ambele?',
    ],
    path: ['Afacere', 'Locație', 'Canal', 'Produs', 'Comandă'],
    equation: {
      salesValue: 'Valoarea vânzărilor',
      orderCount: 'Numărul de comenzi',
      averageOrderValue: 'Valoarea medie a comenzii',
      ariaLabel: 'Valoarea vânzărilor este egală cu numărul de comenzi înmulțit cu valoarea medie a comenzii',
    },
    principle: 'Descompunerea arată UNDE apare diferența. Nu dovedește DE CE a apărut.',
    nextStep: 'Formulează mai multe explicații plauzibile. Apoi caută dovezi care susțin sau contrazic fiecare explicație.',
    evidence: {
      startArea: 'BI Report → Sales',
      secondaryAreas: ['Orders', 'Products', 'Categories'],
      instruction: 'Din Dashboard, folosește doar dovezile relevante pentru întrebare: începe cu BI Report → Sales, apoi verifică Orders, Products sau Categories când ai nevoie de detalii suplimentare.',
      manuals: [
        { label: 'Learn how to use the Sales report', route: GUIDE_ROUTES.manual.sales },
        { label: 'Learn how to use the Orders report', route: GUIDE_ROUTES.manual.orders },
        { label: 'Learn how to use the Product Sales report', route: GUIDE_ROUTES.manual.products },
        { label: 'Learn how to use the Category report', route: GUIDE_ROUTES.manual.categories },
      ],
    },
  },
  domainGroups: [
    {
      key: 'sales-demand',
      name: 'Vânzări și cerere',
      lessons: [
        {
          number: '04',
          lessonKey: 'sales-components',
          title: 'Vânzările se schimbă din cauza numărului de comenzi sau a valorii unei comenzi?',
          summary: 'Separă volumul comenzilor de valoarea fiecărei comenzi.',
          label: 'Lecția 4',
          lead: 'Același rezultat al vânzărilor poate proveni din comportamente foarte diferite ale clienților.',
          reasoningStages: ['Descompune'],
          questions: [
            'Sunt mai puține comenzi?',
            'Este mai mică valoarea medie a unei comenzi?',
            'S-au schimbat ambele?',
            'Una s-a îmbunătățit în timp ce cealaltă s-a deteriorat?',
          ],
          model: {
            type: 'sequence',
            ariaLabel: 'Valoarea vânzărilor este egală cu numărul de comenzi înmulțit cu valoarea medie a unei comenzi',
            items: ['Valoarea vânzărilor', 'Numărul de comenzi', 'Valoarea medie a unei comenzi'],
            separators: ['=', '×'],
          },
          example: {
            heading: 'Vânzările sunt cu aproximativ 8% mai mici.',
            facts: [
              'Numărul de comenzi este cu aproximativ 11% mai mic.',
              'Valoarea medie a unei comenzi este cu aproximativ 3% mai mare.',
            ],
            conclusionLabel: 'Interpretare în această etapă',
            conclusion: 'Principala diferență apare acum în volumul comenzilor, nu în cheltuiala mai mică pe comandă.',
            qualification: 'Aceasta localizează componenta care s-a schimbat. Nu explică de ce numărul de comenzi este mai mic.',
          },
          principle: 'O valoare medie mai bună a comenzii nu înseamnă automat că afacerea merge mai bine dacă s-au pierdut multe comenzi.',
          nextStep: 'Verifică dacă volumul mai mic al comenzilor apare în toată afacerea sau este concentrat în anumite locații, canale ori intervale.',
          evidence: {
            startArea: 'BI Report → Sales',
            secondaryAreas: ['Orders', 'Products', 'Categories', 'Engagement'],
            instruction: 'Deschide Dashboard, apoi alege BI Report → Sales. Compară valoarea vânzărilor, numărul de comenzi și valoarea medie a unei comenzi pentru aceeași perioadă și aceeași arie. Folosește Orders sau celelalte rapoarte doar pentru a testa următoarea ipoteză.',
            manuals: [
              { label: 'Sales Report', route: GUIDE_ROUTES.manual.sales },
              { label: 'Orders Report', route: GUIDE_ROUTES.manual.orders },
              { label: 'Engagement Report', route: GUIDE_ROUTES.manual.engagement },
            ],
          },
        },
        {
          number: '05',
          lessonKey: 'demand-location',
          title: 'Unde se schimbă cererea?',
          summary: 'Găsește locațiile, canalele și intervalele în care apare diferența.',
          label: 'Lecția 5',
          lead: 'Rezultatul întregii companii poate ascunde situații locale foarte diferite.',
          reasoningStages: ['Descompune'],
          questions: [
            'Diferența apare în majoritatea locațiilor?',
            'Este concentrată în doar câteva locații?',
            'Apare mai ales într-un singur canal de vânzare sau livrare?',
            'Apare într-un anumit interval sau într-un tipar legat de zi?',
          ],
          model: {
            type: 'sequence',
            ariaLabel: 'Afacere spre locație spre canal spre timp',
            items: ['Afacere', 'Locație', 'Canal', 'Timp'],
            separators: ['→', '→', '→'],
          },
          example: {
            heading: 'Vânzările rețelei sunt mai mici.',
            facts: [
              'Cea mai mare parte a scăderii vine din trei locații.',
              'În acele locații, diferența principală apare la comenzile cu livrare, în timp ce comenzile din restaurant sunt în mare parte stabile.',
            ],
            conclusionLabel: 'Concluzie în această etapă',
            conclusion: 'Investigația a fost restrânsă la activitatea de livrare din acele locații.',
            qualification: 'Încă nu știm dacă explicația ține de cererea clienților, disponibilitate, program, furnizorul de livrare, execuția restaurantului sau alt factor.',
          },
          principle: 'Mai întâi localizează schimbarea. Apoi investighează de ce s-a produs.',
          nextStep: 'Verifică ce aleg clienții în locațiile și canalele afectate, apoi testează explicațiile operaționale numai acolo unde te conduc informațiile disponibile.',
          evidence: {
            startArea: 'BI Report → Sales',
            secondaryAreas: ['Orders', 'Locations', 'Locations → Schedule', 'Locations → POS', 'OrderHub'],
            instruction: 'Deschide Dashboard, apoi alege BI Report → Sales pentru informațiile despre locație, canalul de livrare și intervalul de timp. Continuă cu Orders. Verifică Locations și filele Schedule sau POS, ori folosește Magic → OrderHub, numai când o ipoteză operațională concretă o cere.',
            manuals: [
              { label: 'Sales Report', route: GUIDE_ROUTES.manual.sales },
              { label: 'Orders Report', route: GUIDE_ROUTES.manual.orders },
              { label: 'Manage Locations', route: GUIDE_ROUTES.manual.locations },
              { label: 'Manage Location Schedules', route: GUIDE_ROUTES.manual.locationSchedules },
              { label: 'Manage Location POS', route: GUIDE_ROUTES.manual.locationPos },
            ],
          },
        },
        {
          number: '06',
          lessonKey: 'customer-choices',
          title: 'Ce aleg diferit clienții?',
          summary: 'Privește împreună valoarea, cantitatea și ponderea înainte să judeci cererea pentru produse.',
          label: 'Lecția 6',
          lead: 'Rezultatul vânzărilor depinde nu doar de câți clienți cumpără, ci și de ceea ce aleg.',
          reasoningStages: ['Descompune', 'Explică'],
          questions: [
            'S-a schimbat valoarea absolută a vânzărilor?',
            'S-a schimbat cantitatea vândută?',
            'S-a schimbat ponderea produsului sau a categoriei?',
            'Tiparul rămâne același când folosim aceeași perioadă și aceeași arie?',
          ],
          model: {
            type: 'sequence',
            ariaLabel: 'Valoarea nu este cantitate, cantitatea nu este pondere, iar ponderea nu este profitabilitate',
            items: ['Valoare', 'Cantitate', 'Pondere', 'Profitabilitate'],
            separators: ['≠', '≠', '≠'],
          },
          example: {
            heading: 'Ponderea unei categorii a crescut de la 20% la 24%.',
            facts: ['Înainte să spui că cererea a crescut, verifică valoarea absolută a vânzărilor categoriei și cantitatea.'],
            conclusionLabel: 'Interpretare posibilă',
            conclusion: 'Ponderea poate fi mai mare deoarece restul portofoliului a scăzut.',
            qualification: 'Un produs cu vânzări mari nu este automat produsul cel mai profitabil.',
          },
          principle: 'Verifică întotdeauna numărul absolut înainte să interpretezi un procent sau o pondere.',
          nextStep: 'Dacă schimbarea este reală, formulează separat ipoteze despre promoție, preț, disponibilitate, stoc sau preferințele clienților. Testează-le în loc să presupui una dintre ele.',
          evidence: {
            startArea: 'BI Report → Products',
            secondaryAreas: ['Categories', 'Sales'],
            instruction: 'Deschide Dashboard, apoi alege BI Report → Products. Compară valoarea și cantitatea, apoi folosește Categories pentru contextul de mix sau pondere și Sales pentru totalul afacerii. Profitabilitatea necesită dovezi economice separate.',
            manuals: [
              { label: 'Product Sales Report', route: GUIDE_ROUTES.manual.products },
              { label: 'Category Report', route: GUIDE_ROUTES.manual.categories },
              { label: 'Sales Report', route: GUIDE_ROUTES.manual.sales },
            ],
          },
        },
      ],
    },
    {
      key: 'customers',
      name: 'Clienți',
      lessons: [
        {
          number: '07',
          lessonKey: 'client-activation',
          title: 'Fac noii clienți înregistrați prima achiziție?',
          summary: 'Evaluează activarea prin prima achiziție după ce cohorta a avut timp să cumpere.',
          label: 'Lecția 7',
          lead: 'Un cont înregistrat devine activ prin cumpărare la prima achiziție eligibilă. Validarea contului este o măsură separată.',
          reasoningStages: ['Compară', 'Descompune'],
          questions: [
            'Ce pondere a înregistrărilor a făcut prima achiziție eligibilă în același interval de zile de la înregistrare?',
            'Câți clienți au parcurs întregul interval și câți sunt încă în așteptare?',
            'Se schimbă tiparul după cohorta de înregistrare sau județul adresei actuale din profil?',
            'Se schimbă a doua și a treia achiziție separat de prima?',
          ],
          model: {
            type: 'sequence',
            ariaLabel: 'Înregistrare spre prima achiziție eligibilă spre achiziții repetate',
            items: ['Înregistrare', 'Prima achiziție', 'Achiziție repetată'],
            separators: ['→', '→'],
          },
          example: {
            heading: 'O cohortă recentă are o rată mică de activare în 30 de zile.',
            facts: ['Mulți clienți sunt încă în așteptare, deoarece nu au trecut 30 de zile de la înregistrare.'],
            conclusionLabel: 'Întrebare de management',
            conclusion: 'Cum arată rata pentru clienții care au avut la dispoziție toate cele 30 de zile?',
            qualification: 'Clienții în așteptare nu sunt conversii ratate. Compară cohorte la fel de mature înainte să judeci o campanie sau un proces.',
          },
          principle: 'Prima achiziție, validarea contului și repetarea achiziției sunt etape diferite.',
          nextStep: 'Compară cohorte mature la același timp de la înregistrare, apoi investighează unde diferă prima achiziție sau progresul spre achiziții repetate.',
          evidence: {
            startArea: 'BI Report → Clients → Activation',
            secondaryAreas: ['BI Report → Clients → Cohorts', 'BI Report → Clients → Overview'],
            instruction: 'Deschide Dashboard, apoi BI Report → Clients → Activation pentru prima achiziție, numărul de clienți eligibili și în așteptare și achizițiile repetate. Folosește Cohorts pentru a compara săptămâni de înregistrare după același număr de zile; filtrul de județ descrie adresa actuală din profilul clientului.',
            manuals: [
              { label: 'Understanding the Clients Report', route: GUIDE_ROUTES.manual.clients },
              { label: 'Working with Reports', route: GUIDE_ROUTES.manual.reports },
            ],
          },
        },
        {
          number: '08',
          lessonKey: 'returning-customers',
          title: 'Revin clienții?',
          summary: 'Separă prima achiziție, cumpărătorii care revin și retenția pe termen lung.',
          label: 'Lecția 8',
          lead: 'Înregistrarea nu înseamnă automat valoare creată de relația cu un client.',
          reasoningStages: ['Compară', 'Explică'],
          questions: [
            'Ce pondere a clienților activi a făcut a doua achiziție eligibilă sau una ulterioară în perioada selectată?',
            'Cum evoluează prima, a doua și a treia achiziție pentru cohorte la fel de mature?',
            'Clienții recenți au avut suficient timp să facă încă o achiziție?',
            'Ce activitate de cumpărare rămâne în afara populației de clienți înregistrați?',
          ],
          model: {
            type: 'sequence',
            ariaLabel: 'Client nou spre client activ spre client care revine',
            items: ['Client nou', 'Client activ', 'Client care revine'],
            separators: ['→', '→'],
          },
          example: {
            heading: 'Numărul înregistrărilor a crescut mult.',
            facts: ['Ponderea clienților activi care revin pentru a doua achiziție sau una ulterioară nu a crescut în același ritm.'],
            conclusionLabel: 'Întrebare de management',
            conclusion: 'Se transformă primele achiziții în relații cu cumpărări repetate?',
            qualification: 'Numărul cumpărătorilor care revin într-o perioadă nu este o rată de retenție pe termen lung. Un client poate apărea atât la prima achiziție, cât și la revenire, dacă a cumpărat de mai multe ori în acea perioadă.',
          },
          principle: 'A doua achiziție este o etapă utilă, dar nu dovedește singură loialitatea.',
          nextStep: 'Folosește mai întâi tiparele agregate. Verifică fișa unui client numai pentru un scop clar, autorizat și care respectă confidențialitatea.',
          evidence: {
            startArea: 'BI Report → Clients → Activation',
            secondaryAreas: ['BI Report → Clients → Cohorts', 'BI Report → Clients → Overview', 'Engagement'],
            instruction: 'Deschide Dashboard, apoi BI Report → Clients. Folosește Overview pentru clienții activi și cei care revin, Activation pentru a doua și a treia achiziție și Cohorts pentru a compara grupuri după același timp de la înregistrare. Engagement arată activitate de vânzare, nu numărul clienților care revin.',
            manuals: [
              { label: 'Understanding the Clients Report', route: GUIDE_ROUTES.manual.clients },
              { label: 'Engagement Report', route: GUIDE_ROUTES.manual.engagement },
              { label: 'Understanding Orders and Clients', route: GUIDE_ROUTES.manual.ordersAndClients },
            ],
          },
        },
        {
          number: '09',
          lessonKey: 'client-engagement',
          title: 'Cine generează activitatea de vânzare?',
          summary: 'Compară comenzile, vânzările și bonul mediu ale activității înregistrate și anonime, fără să confunzi tranzacțiile cu oamenii.',
          label: 'Lecția 9',
          lead: 'Raportul Engagement compară activitatea de vânzare reprezentată pentru cumpărătorii înregistrați și anonimi.',
          reasoningStages: ['Compară', 'Descompune'],
          questions: [
            'Numărul comenzilor înregistrate și anonime s-a schimbat în același fel?',
            'Schimbarea ponderii vânzărilor vine din ponderea comenzilor, din bonul mediu sau din ambele?',
            'Tiparul se păstrează pentru aceeași perioadă și aceleași locații de vânzare?',
            'Ce nu ne poate spune acest raport despre persoane distincte sau vizite?',
          ],
          model: {
            type: 'definitions',
            items: [
              { term: 'Ponderea comenzilor', description: 'Ponderea comenzilor înregistrate în totalul comenzilor reprezentate.' },
              { term: 'Ponderea vânzărilor', description: 'Ponderea valorii vânzărilor înregistrate în totalul reprezentat.' },
              { term: 'Bon mediu', description: 'Valoarea vânzărilor împărțită la numărul comenzilor pentru fiecare grup reprezentat.' },
            ],
          },
          example: {
            heading: 'Ponderea vânzărilor înregistrate crește, dar ponderea comenzilor rămâne stabilă.',
            facts: ['Compară bonul mediu al activității înregistrate și anonime înainte să numești acest rezultat un angajament mai mare.'],
            conclusionLabel: 'Interpretare posibilă',
            conclusion: 'S-a putut schimba valoarea comenzilor fără să existe mai multe comenzi înregistrate.',
            qualification: 'Comenzile numără tranzacții, iar vânzările arată valoarea lor. Niciuna nu măsoară clienți distincți, vizite, retenție sau dacă o campanie a produs schimbarea.',
          },
          principle: 'O pondere mai mare a vânzărilor înregistrate nu dovedește că au cumpărat mai mulți clienți distincți.',
          nextStep: 'Folosește Clients pentru etapele de cumpărare ale clienților înregistrați. Compară atent aria dată de județul adresei actuale din Clients cu aria locației de vânzare din Engagement.',
          evidence: {
            startArea: 'BI Report → Engagement',
            secondaryAreas: ['BI Report → Sales', 'BI Report → Clients'],
            instruction: 'Deschide Dashboard, apoi BI Report → Engagement. Verifică perioada și Sales location, apoi compară comenzile, vânzările, ponderile și bonul mediu pentru activitatea înregistrată și anonimă. Folosește Clients pentru clienți înregistrați distincți și Sales pentru totalul mai larg.',
            manuals: [
              { label: 'Engagement Report', route: GUIDE_ROUTES.manual.engagement },
              { label: 'Understanding the Clients Report', route: GUIDE_ROUTES.manual.clients },
            ],
          },
        },
        {
          number: '10',
          lessonKey: 'client-credit',
          title: 'Ce ne spune creditul clienților?',
          summary: 'Separă mișcările și soldul creditului de valoarea clienților sau profit.',
          label: 'Lecția 10',
          lead: 'Creditul clienților este un sold financiar urmărit separat. Mișcările pot orienta o investigație, dar nu explică singure comportamentul de cumpărare.',
          reasoningStages: ['Observă', 'Explică'],
          questions: [
            'Cât credit a fost acordat, folosit sau modificat prin alte mișcări în perioada selectată?',
            'Care este soldul afișat la sfârșitul perioadei pentru conturile care nu sunt șterse acum și când o dată trecută reprezintă doar o estimare?',
            'Există recompense automate omise sau mișcări neobișnuite care trebuie reconciliate cu comenzile?',
            'Ce nu poate dovedi activitatea creditului despre revenirea clienților, profit sau numerar?',
          ],
          model: {
            type: 'definitions',
            items: [
              { term: 'Rewarded / Used', description: 'Mișcări din perioada selectată.' },
              { term: 'Net', description: 'Suma semnată a mișcărilor, inclusiv alte tipuri.' },
              { term: 'Total credit', description: 'Soldul la sfârșitul perioadei pentru conturile care nu sunt șterse în prezent; datele istorice sunt estimări reconstruite.' },
            ],
          },
          example: {
            heading: 'Creditul acordat crește, iar utilizarea rămâne redusă.',
            facts: ['Soldul neutilizat crește, dar nu știm doar din acest rezultat de ce clienții nu îl folosesc.'],
            conclusionLabel: 'Întrebare de management',
            conclusion: 'Regulile creditului, comenzile eligibile și mișcările înregistrate corespund așteptărilor?',
            qualification: 'Creditul acordat sau păstrat nu este vânzare suplimentară, numerar, profit ori dovadă de loialitate. Estimările istorice depind de un istoric complet.',
          },
          principle: 'Reconciliază creditul ca sold și istoric al mișcărilor înainte să tragi concluzii comerciale.',
          warning: 'Folosește mai întâi date agregate. Căutarea unui client și orice ajustare manuală cer fluxul autorizat de finanțe și confidențialitate.',
          nextStep: 'Compară activitatea agregată a creditului cu comenzile și cumpărările clienților înregistrați. Escaladează orice diferență între sold și istoric prin procesul autorizat.',
          evidence: {
            startArea: 'Magic → ClientCredit',
            secondaryAreas: ['BI Report → Clients', 'BI Report → Orders'],
            instruction: 'Deschide Magic → ClientCredit pentru Rewarded, Used, Net, Total credit, evoluția zilnică și excepțiile recompenselor. Citește Total credit ca sold la data de sfârșit; datele trecute sunt estimări. Soldurile din Top 5 sunt întotdeauna actuale, chiar dacă alegi o zi pentru clasamentul activității. Folosește manualul asociat pentru lucrul autorizat cu un client.',
            manuals: [
              { label: 'Manage Client Credit', route: GUIDE_ROUTES.manual.clientCredit },
              { label: 'Understanding the Clients Report', route: GUIDE_ROUTES.manual.clients },
            ],
          },
        },
      ],
    },
    {
      key: 'operations',
      name: 'Operațiuni',
      lessons: [
        {
          number: '11',
          lessonKey: 'delivery-reliability',
          title: 'Poate restaurantul să ofere ceea ce așteaptă clienții?',
          summary: 'Caută informații operaționale și din partea clienților care pot contrazice rezultatul vânzărilor.',
          label: 'Lecția 11',
          lead: 'Cererea bună nu este suficientă dacă restaurantul nu poate funcționa în mod fiabil.',
          reasoningStages: ['Explică'],
          questions: [
            'Clienții pot comanda ceea ce promitem?',
            'Comenzile evoluează normal?',
            'Anulările, stornările sau excepțiile sunt în creștere?',
            'Este indisponibilă o locație sau o cale prin care se pot plasa comenzi?',
            'Feedbackul clienților contrazice rezultatul comercial?',
          ],
          example: {
            heading: 'Vânzările sunt aproape de ținta stabilită.',
            facts: [
              'Feedbackul clienților s-a deteriorat.',
              'O locație are un număr neobișnuit de mare de excepții la comenzi.',
            ],
            conclusionLabel: 'Concluzie',
            conclusion: 'Rezultatul comercial nu este suficient pentru a declara că operațiunea este sănătoasă.',
            qualification: 'Folosește fiecare semnal operațional pentru a testa o ipoteză concretă; nu combina toate excepțiile într-un singur diagnostic.',
          },
          principle: 'Un rezultat bun al vânzărilor poate ascunde deteriorarea experienței clienților sau o problemă operațională.',
          nextStep: 'Restrânge problema după locație, canal, tipar al comenzilor sau temă din feedbackul clienților. Folosește Logs numai pentru confirmare de specialitate, după ce dovezile principale sunt clare.',
          evidence: {
            startArea: 'BI Report → Orders',
            secondaryAreas: ['OrderHub', 'Dashboard', 'Feedback'],
            instruction: 'Deschide Dashboard, apoi alege BI Report → Orders pentru excepțiile agregate. Folosește Magic → OrderHub pentru investigarea clar definită a unei comenzi și Magic → Feedback pentru tipare în evaluări sau comentarii. Păstrează Logs → POS și Logs → Platform pentru confirmarea făcută de specialiști autorizați.',
            manuals: [
              { label: 'Orders Report', route: GUIDE_ROUTES.manual.orders },
              { label: 'Manage Orders in OrderHub', route: GUIDE_ROUTES.manual.orderHub },
              { label: 'Review Order Feedback', route: GUIDE_ROUTES.manual.feedback },
              { label: 'Dashboard Overview', route: GUIDE_ROUTES.manual.dashboard },
            ],
          },
        },
      ],
    },
    {
      key: 'economics',
      name: 'Rezultate economice',
      lessons: [
        {
          number: '12',
          lessonKey: 'economic-result',
          title: 'Avem vânzări mai mari — dar suntem într-adevăr mai bine?',
          summary: 'Separă veniturile, costul direct reprezentat, contribuția, profitul și numerarul.',
          label: 'Lecția 12',
          lead: 'Veniturile nu sunt același lucru cu profitul.',
          reasoningStages: ['Compară', 'Explică'],
          questions: [
            'S-au schimbat veniturile?',
            'Costul direct reprezentat al produselor s-a schimbat mai repede sau mai lent decât veniturile?',
            'Mixul de produse a schimbat contribuția reprezentată?',
            'Procentul marjei și marja în lei s-au schimbat în direcții diferite?',
            'Ce produse, cantități de ingrediente sau schimbări de preț ale ingredientelor au cel mai mare impact financiar?',
            'Ce costuri importante sau mișcări de numerar lipsesc din aceste informații?',
          ],
          model: {
            type: 'definitions',
            items: [
              { term: 'Vânzări / venituri', description: 'Cât au plătit clienții.' },
              { term: 'Cost direct al produselor', description: 'Costul reprezentat al produselor vândute.' },
              { term: 'Contribuție / marjă brută', description: 'Ce rămâne după costul direct reprezentat al produselor, înainte de multe alte costuri ale afacerii.' },
              { term: 'Procentul marjei', description: 'Marja brută împărțită la vânzările fără TVA. Media locației este ponderată cu vânzările, astfel încât produsele cu o pondere mai mare influențează mai mult rezultatul.' },
              { term: 'Profit net', description: 'Necesită costuri suplimentare și informații contabile.' },
              { term: 'Numerar', description: 'Nu este același lucru cu profitul.' },
            ],
          },
          example: {
            heading: 'Vânzările lunare fără TVA cresc de la 100.000 lei la 120.000 lei.',
            facts: [
              'Costul produselor crește de la 60.000 lei la 78.000 lei.',
              'Marja scade de la 40% la 35%, iar valoarea ei crește de la 40.000 lei la 42.000 lei.',
            ],
            conclusionLabel: 'Concluzie',
            conclusion: 'Un procent mai mic al marjei poate însoți o marjă mai mare în lei. Citește ambii indicatori, apoi separă prețul de vânzare realizat, volumul/mixul de produse și costul unitar înainte să alegi ce schimbi.',
            qualification: 'Răspunsul financiar complet poate necesita informații din ERP, contabilitate, salarizare, bănci sau alte sisteme de specialitate din afara Kalopsia.',
          },
          principle: 'Veniturile îți spun cât ai vândut. Nu îți spun cât ai câștigat.',
          distinctions: [
            'Produsele peste sau sub marja medie a locației în luna analizată răspund la altă întrebare decât produsele care explică schimbarea față de luna precedentă. Prioritizează impactul financiar ponderat cu vânzările, nu un procent extrem la un produs cu vânzări mici.',
            'Comparația Cost Qty cu Rcp Qty folosește rețeta curentă drept reper și include pierderi și plusuri repartizate. O diferență merită verificată, dar nu dovedește risipa cauzată de un anumit produs. Compară separat evaluarea ingredientelor cu luna precedentă.',
            'Vizualizările pentru combo-uri, canale și promoții descriu aceleași vânzări din perspective diferite. Păstrează valorile lor separat de totalurile produselor componente.',
            'O valoare indisponibilă este necunoscută și nu trebuie interpretată ca zero. Verifică zilele lipsă, costurile produselor și acoperirea comparației înainte să tragi o concluzie despre întreaga lună.',
          ],
          nextStep: 'Începe cu cele mai mari influențe pozitive și negative. Verifică informațiile despre preț, volum/mix, cost și ingrediente; alege două sau trei verificări practice și urmărește rezultatul luna următoare. Marja brută a produselor exclude costuri precum chiria, salariile și comisioanele platformelor, deci nu stabilește profitul net sau numerarul.',
          evidence: {
            startArea: 'BI Report → Commercial → Margin',
            secondaryAreas: ['BI Report → Sales', 'BI Report → ProdCost → Product Margin, când utilizatorul este autorizat și informațiile despre cost sunt potrivite'],
            instruction: 'Pentru utilizatorii cu acces la rapoartele de marjă, deschide Dashboard → BI Report → Commercial → Margin. Alege o locație, o lună completă cu închidere de lună înregistrată și limba raportului. Luna calendaristică precedentă este comparată când informațiile permit. Alege Generate PDF, apoi deschide rezultatul salvat pentru comparația produselor și vizualizările separate de ingrediente, combo-uri, canale și promoții. Descarcă PDF-ul în engleză sau română din istoricul salvat în maximum 24 de ore. Folosește Sales, Product Margin și vizualizările de cost/rețete pentru verificări; citește acoperirea când lipsesc valori sau comparații.',
            manuals: [
              { label: 'Sales Report', route: GUIDE_ROUTES.manual.sales },
              { label: 'Understanding Inventory, Cost, and Supply', route: GUIDE_ROUTES.manual.inventoryCostSupply },
              { label: 'Manage Product Cost and Recipes', route: GUIDE_ROUTES.manual.productCostRecipes },
            ],
          },
        },
      ],
    },
    {
      key: 'commercial',
      name: 'Commercial',
      lessons: [
        {
          number: '13',
          lessonKey: 'commercial-objective',
          orderedQuestions: true,
          title: 'Acțiunea comercială a avut rezultatul dorit?',
          summary: 'Definește rezultatul dorit înainte să evaluezi o promoție, o schimbare de preț, o lansare sau o altă acțiune comercială.',
          label: 'Lecția 13',
          lead: 'O promoție, o schimbare de preț, lansarea unui produs, o decizie de disponibilitate, o inițiativă de canal sau o campanie pentru clienți poate fi evaluată numai în raport cu rezultatul urmărit.',
          reasoningStages: ['Explică', 'Decide', 'Verifică'],
          questions: [
            'Ce rezultat ne așteptam să obținem: mai multe comenzi sau vizite, clienți noi ori reactivați, testarea unui produs, schimbarea mixului, cerere mai bună într-o perioadă slabă sau un alt obiectiv clar?',
            'Ce clienți, produse, locații sau canale au fost vizate?',
            'Ce s-a schimbat după acțiune?',
            'Comparativ cu ce?',
            'Ce altceva ar putea explica schimbarea?',
            'Care a fost costul economic sau costul pentru client?',
            'S-a deteriorat altceva?',
            'Ar trebui să continuăm, să modificăm, să oprim sau să investigăm mai mult?',
          ],
          example: {
            heading: 'O promoție arată 1.000 de revendicări folosite și comenzi asociate în valoare de 80.000 RON.',
            facts: [
              'Folosirea promoției nu este același lucru cu succesul: faptul că o promoție a fost folosită nu înseamnă automat că a îmbunătățit afacerea.',
              'Aceasta arată că promoția a fost folosită de clienți.',
              'Arată și că acele comenzi au fost asociate promoției.',
            ],
            limitLabel: 'Aceasta nu dovedește că:',
            limits: [
              'acele comenzi nu s-ar fi făcut fără promoție;',
              'a crescut frecvența clienților;',
              'promoția a generat vânzări suplimentare;',
              'nu au fost înlocuite vânzările altui produs;',
              'discountul a fost justificat economic.',
            ],
            conclusionLabel: 'Revino la obiectiv',
            conclusion: 'Dacă obiectivul a fost testarea unui produs, întreabă dacă acei clienți l-au cumpărat din nou mai târziu. Dacă obiectivul a fost creșterea cererii într-o perioadă slabă, compară perioada cu o bază de referință potrivită.',
            qualification: 'Kalopsia poate arăta activitatea și asocierea, dar informațiile disponibile acum nu oferă un răspuns cauzal complet.',
          },
          principle: 'Definește rezultatul dorit înainte să măsori succesul.',
          warning: 'O asociere nu dovedește automat o relație de cauzalitate.',
          nextStep: 'Alege informațiile care corespund obiectivului inițial, ia în calcul alte explicații și costurile, apoi decide dacă acțiunea trebuie continuată, modificată, oprită sau investigată mai mult. Stabilește cum vei verifica decizia.',
          evidence: {
            startArea: 'BI Report → Promotions',
            secondaryAreas: ['BI Report → Commercial', 'BI Report → Commercial → Margin', 'Sales', 'Orders', 'Clients', 'Engagement', 'Products', 'Categories'],
            instruction: 'Deschide Dashboard, apoi alege BI Report → Promotions pentru folosirea promoției, valoarea comenzilor asociate, discounturi și revendicări deschise. Folosește Commercial → Products pentru un produs, combo sau o promoție selectată, pe săptămâni complete. Folosește Commercial → Margin pentru o lună completă închisă la o locație: vizualizarea Promotions pune alături discounturile înregistrate și vânzările comenzilor eligibile, procentul marjei, marja în lei și schimbările față de luna precedentă. Fiecare comandă eligibilă este numărată o singură dată, iar reducerile sunt deja incluse în vânzările fără TVA. Verifică acoperirea și celelalte rapoarte potrivit obiectivului. Aceste observații nu dovedesc cauza, vânzările suplimentare sau profitul net.',
            manuals: [
              { label: 'Promotion Report', route: GUIDE_ROUTES.manual.promotions },
              { label: 'Dashboard Overview', route: GUIDE_ROUTES.manual.dashboard },
              { label: 'Sales Report', route: GUIDE_ROUTES.manual.sales },
              { label: 'Orders Report', route: GUIDE_ROUTES.manual.orders },
              { label: 'Understanding the Clients Report', route: GUIDE_ROUTES.manual.clients },
              { label: 'Engagement Report', route: GUIDE_ROUTES.manual.engagement },
            ],
          },
        },
      ],
    },
    {
      key: 'intelligence',
      name: 'Intelligence',
      lessons: [
        {
          number: '14',
          lessonKey: 'missing-signals',
          orderedQuestions: true,
          title: 'Ce s-ar putea să nu observ?',
          summary: 'Folosește semnalele neobișnuite pentru a-ți orienta atenția, apoi revino la raționamentul managerial obișnuit.',
          label: 'Lecția 14',
          lead: 'Managerii tind să investigheze lucrurile pe care știu deja să le caute. Intelligence poate semnala altceva ce merită atenție, dar nu înlocuiește înțelegerea sau judecata.',
          reasoningStages: ['Observă', 'Compară'],
          questions: [
            'Informațiile sunt fiabile și suficient de complete?',
            'Diferența este suficient de importantă încât să merite atenție?',
            'Unde apare?',
            'Ce ar putea să o explice?',
            'Ce informație importantă ar putea lipsi?',
            'Este necesară o acțiune?',
            'Cum vom verifica decizia?',
          ],
          model: {
            type: 'sequence',
            ariaLabel: 'Este detectat un semnal neobișnuit, apoi verificăm fiabilitatea, importanța, locul, explicațiile, contextul lipsă, acțiunea și verificarea',
            items: ['Semnal neobișnuit', 'Informații fiabile?', 'Diferență importantă?', 'Unde?', 'Explicații posibile?', 'Context lipsă?', 'Acțiune?', 'Verificare?'],
            separators: ['→', '→', '→', '→', '→', '→', '→'],
          },
          example: {
            heading: 'Kalopsia identifică vânzări neobișnuit de mici la o locație față de tiparul așteptat.',
            facts: [
              'Nu concluziona direct că a scăzut cererea.',
              'Deschide explicația vânzărilor pentru a separa tranzacțiile înregistrate, bonul mediu și informațiile disponibile la nivel de produs. Verifică apoi canalele, disponibilitatea, contextul operațional, datele incomplete și evenimentele locale.',
            ],
            conclusionLabel: 'Ce ne spune semnalul',
            conclusion: 'Poate fi util să ne îndreptăm atenția spre această locație și perioadă.',
            qualification: 'Insight-ul este un semnal de pornire. Nu explică motivul și nu înlocuiește investigația.',
          },
          distinctions: [
            'Neobișnuit nu înseamnă automat rău.',
            'O anomalie nu explică motivul apariției sale.',
            'Un insight nu justifică automat o acțiune.',
            'Starea unui episod de avertizare privind tranzacțiile și starea analizei făcute de o persoană răspund la întrebări diferite.',
          ],
          principle: 'Intelligence îți arată unde merită să te uiți. Decizia are nevoie în continuare de înțelegere.',
          warning: 'Componentele observate ale schimbării vânzărilor și scorul calității dovezilor nu reprezintă o cauză demonstrată sau o probabilitate. Întreabă ce informații importante lipsesc din sistem, precum schimbări operaționale ori evenimente locale.',
          nextStep: 'Revino la întregul circuit: Observă → Compară → Descompune → Explică → Decide → Verifică. Testează semnalul cu informații fiabile, ia în calcul contextul lipsă, alege numai o acțiune proporțională și stabilește cum o vei verifica.',
          evidence: {
            startArea: 'Dashboard → intel → Intelligence inbox',
            secondaryAreas: ['Dashboard → trends', 'BI Report → Sales', 'BI Report → Orders', 'Locations'],
            instruction: 'Deschide Dashboard → intel pentru rezumate zilnice, semnale de vânzări peste sau sub nivelul așteptat și avertizări Transactions. Semnalele de vânzări oferă o explicație a componentelor observate; avertizările privind tranzacțiile pot deschide Trends. Starea analizei consemnează verificarea umană, iar un episod în desfășurare sau încheiat descrie tendința înregistrată. Investigă folosind rapoarte și informații locale înainte să acționezi.',
            manuals: [
              { label: 'Dashboard Overview', route: GUIDE_ROUTES.manual.dashboard },
              { label: 'Sales Report', route: GUIDE_ROUTES.manual.sales },
              { label: 'Orders Report', route: GUIDE_ROUTES.manual.orders },
              { label: 'Product Sales Report', route: GUIDE_ROUTES.manual.products },
              { label: 'Working with Reports', route: GUIDE_ROUTES.manual.reports },
            ],
          },
        },
        {
          number: '15',
          lessonKey: 'sales-trends',
          title: 'Este scăderea persistentă sau locală?',
          summary: 'Folosește săptămâni complete și comparabile pentru a vedea dacă scăderea tranzacțiilor persistă la o locație.',
          label: 'Lecția 15',
          lead: 'Vânzările totale pot rămâne stabile când sunt mai puține tranzacții, dar bonul mediu este mai mare. Tendința săptămânală ajută la localizarea tiparului.',
          reasoningStages: ['Compară', 'Descompune'],
          questions: [
            'Cum s-au schimbat vânzările, tranzacțiile înregistrate și bonul mediu în cele șase săptămâni complete?',
            'Au fost comparabile aceleași zile ale săptămânii din anul trecut și câte săptămâni au fost incluse?',
            'Tiparul ține de o singură locație sau apare și la locații comparabile din rețea?',
            'Sunt îndeplinite criteriile avertizării, există un episod înregistrat și ce a verificat o persoană?',
          ],
          model: {
            type: 'sequence',
            ariaLabel: 'Schimbare săptămânală observată spre acoperire spre istoricul episoadelor spre investigație',
            items: ['Schimbare săptămânală', 'Acoperire', 'Istoricul episoadelor', 'Investigație'],
            separators: ['→', '→', '→'],
          },
          example: {
            heading: 'Vânzările sunt aproape stabile, dar tranzacțiile au scăzut în mai multe săptămâni comparabile.',
            facts: ['Bonul mediu este mai mare și poate compensa mai puține tranzacții în totalul vânzărilor.'],
            conclusionLabel: 'Ce ne spune tendința',
            conclusion: 'Locația merită analizată mai atent pentru volumul tranzacțiilor și săptămânile în care a apărut schimbarea.',
            qualification: 'Tranzacțiile înregistrate nu sunt clienți distincți sau trafic pietonal. Tendința nu identifică motivul, iar îndeplinirea criteriilor nu înseamnă automat o problemă deschisă.',
          },
          distinctions: [
            'Săptămânile excluse nu reprezintă activitate zero.',
            'Mai puțin de patru săptămâni comparabile nu sunt suficiente pentru o avertizare nouă.',
            'Starea unui episod, în desfășurare sau încheiat, este separată de starea analizei umane.',
          ],
          principle: 'Verifică acoperirea săptămânilor comparabile și tranzacțiile de bază înainte să interpretezi o tendință.',
          nextStep: 'Deschide dovezile Weeks și History ale locației, apoi testează explicații posibile despre operațiuni și cerere folosind Sales, Orders și contextul local.',
          evidence: {
            startArea: 'Dashboard → trends → Sales trends',
            secondaryAreas: ['Dashboard → intel', 'BI Report → Sales', 'BI Report → Orders'],
            instruction: 'Deschide Dashboard → trends. Alege o duminică încheiată și o locație; compară șase săptămâni complete cu aceleași zile din anul trecut. Citește Sales, Transactions, Avg ticket și Coverage. Când este disponibil, Vs network compară schimbarea tranzacțiilor cu cel puțin cinci locații permise din aceeași companie. Deschide Weeks și History pentru detalii. Vizualizarea Trends nu creează un insight și nu îi schimbă starea analizei.',
            manuals: [
              { label: 'Dashboard Overview', route: GUIDE_ROUTES.manual.dashboard },
              { label: 'Sales Report', route: GUIDE_ROUTES.manual.sales },
              { label: 'Orders Report', route: GUIDE_ROUTES.manual.orders },
            ],
          },
        },
      ],
    },
  ],
  futureDomains: [
    { name: 'Stoc și aprovizionare', description: 'Înțelege dacă cererea poate fi acoperită și dacă informațiile despre stoc sunt sub control.' },
  ],
  reminder: [
    { name: 'Perioadă', question: 'Privesc intervalul de timp corect?' },
    { name: 'Aria analizată', question: 'Privesc locațiile sau grupul de clienți potrivit?' },
    { name: 'Informații', question: 'Datele sunt suficient de complete?' },
    { name: 'Context', question: 'Există ceva important pe care Kalopsia nu îl cunoaște?' },
  ],
};

export const MANAGEMENT_GUIDE_CONTENT_BY_LANGUAGE = {
  en: englishContent,
  ro: romanianContent,
};

// Preserve the original export for consumers that explicitly need English.
export const MANAGEMENT_GUIDE_CONTENT = englishContent;
